import axiosInstance from "@helper/interceptor";

export const getAllSensors = async (params = {}) => {
  const { page = 1, pageSize = 100, search = "" } = params;
  let response = await axiosInstance.get("sensors/", {
    params: {
      page,
      page_size: pageSize,
      search,
    },
    timeout: 30000, // 30 second timeout for large datasets
  });
  return response.data;
};

// For backward compatibility and background loading - get ALL sensors in batches
export const getAllSensorsBatched = async () => {
  let allSensors = [];
  let page = 1;
  let hasMore = true;
  const pageSize = 100;

  while (hasMore) {
    try {
      const response = await getAllSensors({ page, pageSize });

      if (response.results) {
        // Django REST pagination format
        allSensors = [...allSensors, ...response.results];
        hasMore = response.next !== null;
      } else {
        // Non-paginated response (fallback)
        allSensors = response;
        hasMore = false;
      }

      page++;

      // Add small delay between batches to prevent overwhelming server
      if (hasMore) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.warn(`Failed to load sensors page ${page}:`, error);
      hasMore = false;
    }
  }

  return allSensors;
};
export const getSensorsProject = async (id) => {
  let response = await axiosInstance.get(`project/${id}/sensors`);
  return response.data;
};
export const getSensor = async (id) => {
  let response = await axiosInstance.get(`sensors/${id}`);
  return response.data;
};

export const getLastSend = async (id) => {
  let response = await axiosInstance.get(`last-send/${id}`);
  return response.data;
};

export const getInfluxData = async (start, stop, interval, hardwareSerial) => {
  let response = await axiosInstance.get(
    `api/influx-data/?start=${start}&stop=${stop}&interval=${interval}&hardwareSerial=${hardwareSerial}`
  );
  return response.data;
};

export const createSensor = async (formData) => {
  const response = await axiosInstance.post("sensors/", formData);
  return response;
};

export const updateSensor = async ({ updateFormData, detail }) => {
  try {
    const response = await axiosInstance.put(`sensors/${detail.id}/`, updateFormData);
    return response;
  } catch (error) {
    throw error.response.data;
  }
};
export const deleteSensor = async (id) => {
  try {
    const response = await axiosInstance.delete(`sensors/${id}`);
    return response;
  } catch (error) {
    throw new Error({
      message: "Can't delete this sensor.There are some tree linked with this sensor",
    });
  }
};

export const sensorPlot = async (sensor) => {
  let type = "resistance";
  if (sensor.soil_moisture_sensor) {
    type = "water_soil";
  } else if (sensor.dendrometer_sensor) {
    type = "dendrometer";
  } else if (sensor.soil_water_tension_sensor) {
    type = "soil_water_tension";
  }
  const response = await axiosInstance.get(`ts_data/${sensor.id}-${type}`);
  return response.data;
};

export const installationPlot = async (installation) => {
  let type = "resistance";
  if (installation.sensor.soil_moisture_sensor) {
    type = "water_soil";
  } else if (installation.sensor.dendrometer_sensor) {
    type = "dendrometer";
  } else if (installation.sensor.soil_water_tension_sensor) {
    type = "soil_water_tension";
  }
  let response = await axiosInstance.get(
    `data/${installation.tree.id}+${installation.sensor.id}-${type}`
  );
  response.data.x = response.data.x[installation.tree.id];
  response.data.y = response.data.y[installation.tree.id];
  return response.data;
};

export const lookupSensorByTui = async (tuiPart) => {
  const payload = {
    tui_part: tuiPart,
  };
  const response = await axiosInstance.post("internal/sensors/lookup/", payload);
  return response.data;
};

export const assignSensor = async ({ tuiPart, cNamePart, assignedAt, unassignedAt }) => {
  const isNumber = !isNaN(cNamePart) && cNamePart !== "";

  const payload = {
    tui_part: tuiPart,
    ...(isNumber ? { customer_id: Number(cNamePart) } : { customer_name_part: cNamePart }),
  };

  // Add optional assignment dates if provided
  if (assignedAt) {
    payload.assigned_at = assignedAt;
  }
  if (unassignedAt) {
    payload.unassigned_at = unassignedAt;
  }

  const response = await axiosInstance.post("internal/sensors/assign/", payload);
  return response.data;
};

export const createCustomer = async ({ cName, pNumber, cDomain }) => {
  const payload = {
    name: cName,
    package: pNumber,
    domain: cDomain,
  };
  const response = await axiosInstance.post("internal/customers/create/", payload);
  return response.data;
};

export const getCustomerSensors = async ({ cNamePart }) => {
  const isNumber = !isNaN(Number(cNamePart));
  const payload = isNumber
    ? { customer_id: Number(cNamePart) } // fixed typo
    : { customer_name_part: cNamePart };

  const response = await axiosInstance.post("internal/customers/sensors/", payload);

  return response.data;
};

export const writeSensorEvent = async ({ tuiPart, qc, usage, comment }) => {
  const payload = {
    tui_part: tuiPart,
    comments: comment,
  };

  if (qc === true || qc === false) {
    payload.qc_passed = qc;
  }

  const response = await axiosInstance.post("internal/sensor-events/create/", payload);
  return response.data;
};

export const fetchDownloadTreeData = async ({ tree, sensors, parameterQuery }) => {
  const tsps = sensors.map((sensor) => `${tree.id}+${sensor.id}`);
  const tree_sensors_joined = tsps.join(",");
  const res = await axiosInstance.get(`data/${tree_sensors_joined}-${parameterQuery}/download`);
  return res.data;
};

export const fetchDownloadSensorData = async ({ sensors, parameterQuery }) => {
  const res = await axiosInstance.get(`ts_data/${sensors}-${parameterQuery}/download`);
  return res.data;
};

export const unassignSensor = async ({ tuiPart, unassignedAt }) => {
  const payload = {
    tui_part: tuiPart,
    unassigned_at: unassignedAt,
  };
  const response = await axiosInstance.post("internal/sensors/unassign/", payload);
  return response.data;
};

export const setSensorUsage = async ({ tui, usage, customerId }) => {
  const payload = {
    tui,
    usage,
    ...(customerId ? { customer_id: customerId } : {}),
  };
  const response = await axiosInstance.post("internal/sensors/set-usage/", payload);
  return response.data;
};
